/*global define */
/*jshint devel:true*/
/**
 * Base class that is shared across the Backbone Model, View, Controller and Collection base classes
 */
define(
	'BaseClass',[
		'jquery',
		'underscore',
		'backbone'
	],
	function ($, _, Backbone) {
		'use strict';
		/**
		 * @class
		 * @name BaseClass
		 */
		return {
			/**
			 * Name of the current class. Used to build debug messages.
			 * @type {String}
			 */
			className: 'unknownClass',
			/**
			 * Formats a debug message.
			 *
			 * @example
			 * // assuming that this.className == 'BaseModel'
			 * this.report('init', 'this is a message');
			 * -> BaseModel.init(): this is a message
			 *
			 * @example
			 * // assuming that this.className == 'BaseModel'
			 * this.report('this is a message');
			 * -> BaseModel: this is a message
			 *
			 * @example
			 * // assuming that this.className == 'BaseModel'
			 * this.report('', 'this is a message');
			 * -> BaseModel.unknownMethod(): this is a message
			 *
			 * @param {String} method Method/function name
			 * @param {String} msg Message to format.
			 * @return {String} Formatted message
			 */
			report: function (method, msg) {
				var ret = this.className,
					argc = arguments.length;
				if (argc === 1) {
					ret += ': ' + method;
				} else if (argc > 1) {
					ret += '.' + (method ? method : 'unknownMethod') + '(): ' + msg;
				}
				return ret;
			},

			/**
			 * Coerce a model attribute value to the right class.
			 * Additionally, this will coerce the value even when the attribute is being set.
			 * @example Assuming that the attribute comic was to be of type ComicModel:
			 * this.set('comic', {sponge: 'bob'}); --> will attempt to save ComicModel({sponge: 'bob'}) into 'comic'
			 *
			 * @see coerceAttrToModel()
			 * @param {String} attrName Name of the model attribute to coerce.
			 * @param {Function|Backbone.Model|Backbone.Collection} ModelConstructor
			 * @param {Object} [options] Options
			 * @param {boolean} [options.silent] Default value is true. Use {silent: false} if you don't want to set the attribute value silently.
			 * @param {Object} [options.baseObjectDefinition] Supply an object to use as the constructor's root object definition.
			 * @param {boolean} [options.allowEmptyModels] Default value is true. When true, if the value is null or undefined, return an empty model/collection made from ModelConstructor instead.
			 * @return {Backbone.Model} Returns current model instance, for chainability.
			 */
			alwaysCoerceAttrToModel: function (attrName, ModelConstructor, options) {
				if (!options) {
					options = {};
				}

				var self = this,
					methodName = 'alwaysCoerceAttrToModel',
					BackboneCollection = Backbone.Collection,
					baseObjectDefinition = options.baseObjectDefinition,
					isTargetClassBackboneCollection = (ModelConstructor === BackboneCollection) || (ModelConstructor.prototype instanceof BackboneCollection);

				if (!('silent' in options)) {
					options.silent = true;
				}

				if (self instanceof Backbone.Model) { // currently only supporting coercion for Backbone Models

					// coerce attribute value at first run
					//self.coerceAttrToModel(attrName, ModelConstructor, {silent:true}, baseObjectDefinition);

					//self.on('change:' + attrName, coerce);

					var previousSetFc = self.set;
					self.set = function (key, val, setOptions) {
						//DEBUG
						//console.group('coerce set: %o', attrName);

						var myself = this, attrs;

						// Handle both `"key", value` and `{key: value}` -style arguments.
						if (_.isObject(key)) {
							attrs = _.extend({}, key);
							setOptions = val;
						} else {
							attrs = {};
							attrs[key] = val;
						}

						if (attrName in attrs) { // if we have to update the existing value
							var curValue = myself.get(attrName),
								newValue = attrs[attrName];

							//DEBUG
							//console.log('we have to update the attribute %o on %o: curValue=%o, newValue=%o', attrName, this, curValue, newValue);
							//console.log('attrs=', JSON.parse(JSON.stringify(attrs)));
							//console.log('current attributes');
							//console.dir(JSON.parse(JSON.stringify(myself.attributes)));

							if (curValue && (curValue instanceof ModelConstructor)) { // if the current value is already from the right class

								//DEBUG
								//console.log('-> same value type');

								//DEBUG
								//console.group();
								//console.log('before object = %o', curValue);
								//console.log('curValue.attributes=');
								//console.dir( JSON.parse(JSON.stringify( curValue.attributes || {} )));

								if (isTargetClassBackboneCollection) {
									curValue.set(newValue || [], setOptions);
								} else {

									//DEBUG
									//console.log('setting object with: ', JSON.parse(JSON.stringify( newValue || {} )));

									// if the new value is already from the right model class, we just need to set its attributes onto the target model object (curValue)
									if (newValue instanceof ModelConstructor) {
										newValue = newValue.toJSON();
									}
									curValue.set(newValue || {}, setOptions);
								}

								//DEBUG
								//console.log('updated object = %o', curValue);
								//console.log('curValue.attributes=');
								//console.dir( JSON.parse(JSON.stringify( curValue.attributes || {} )));
								//console.groupEnd();

								delete attrs[attrName]; // remove it from the list of attributes to set

								//DEBUG
								//console.log('remove it from the list of attributes to set attrs= %o', attrs);

							} else { // otherwise, coerce it

								//DEBUG
								//console.log('-> coerce it: before=', newValue);

								newValue = myself.coerceValueToModel(newValue, ModelConstructor, options);

								//DEBUG
								//console.log('-> coerce it: after=', newValue);

								attrs[attrName] = newValue;
							}

						}

						//DEBUG
						//console.log('use previous .set() method with attrs=%o setOptions=%o', attrs, setOptions);

						// use previous .set() method
						var ret = previousSetFc.call(myself, attrs, setOptions);

						//console.log('final attributes');
						//console.dir(JSON.parse(JSON.stringify(myself.attributes)));

						//console.groupEnd();
						return ret;
					}; // end of self.set = function(key, val, setOptions)

					self.set(attrName, self.get(attrName), options);
				} else { // not supported yet
					throw new Error(self.report(methodName, 'coercion for this object is not yet supported'));
				}

				return self;
			},

			/**
			 * Coerce a model attribute value to the right class.
			 * This is particularly useful for models that contain nested backbone models.
			 * The value of the model attribute can either be a default object or undefined.
			 * If it's an object, it'll be used as argument of the ModelConstructor (and also override the baseObjectDefinition argument)
			 * @param {String} attrName Name of the model attribute to coerce.
			 * @param {Function|Backbone.Model|Backbone.Collection} ModelConstructor
			 * @param {Object} [options] Options
			 * @param {boolean} [options.silent] Default value is true. Use {silent: false} if you don't want to set the attribute value silently.
			 * @param {Object} [options.baseObjectDefinition] Supply an object to use as the constructor's root object definition.
			 * @param {boolean} [options.allowEmptyModels] Default value is true. When true, if the value is null or undefined, return an empty model/collection made from ModelConstructor instead.
			 * @return {*} Returns current model instance, for chainability.
			 */
			coerceAttrToModel: function (attrName, ModelConstructor, options) {
				var self = this,
					methodName = 'coerceAttrToModel';

				if (!options) {
					options = {};
				}

				if (!('silent' in options)) {
					options.silent = true;
				}

				//DEBUG
				//console.log(self.report(methodName, 'attrName=%o ModelConstructor=%o'), attrName, ModelConstructor);

				var isBackboneModel = self instanceof Backbone.Model,
					attrValue = isBackboneModel ? self.get(attrName) : self[attrName],
					newValue = self.coerceValueToModel(attrValue, ModelConstructor, options);


				if (attrValue !== newValue) {
					// check instance type before assigning attribute
					if (self instanceof Backbone.Model) { // if it's a backbone model
						self.set(attrName, newValue, options);
					} else {
						self[attrName] = newValue;
					}
				}

				return self;
			}, // end of coerceAttrToModel: function(attrName, ModelConstructor, baseObjectDefinition)

			/**
			 * Coerce a given value to a specific class (can be a backbone Model or Collection too)
			 * @param {*} value Value to convert.
			 * @param {Function|Backbone.Model|Backbone.Collection} ModelConstructor
			 * @param {Object} [options] Defines more options.
			 * @param {Object} [options.baseObjectDefinition] Supply an object to use as the constructor's root object definition.
			 * @param {boolean} [options.allowEmptyModels] Default value is true. When true, if the value is null or undefined, return an empty model/collection made from ModelConstructor instead.
			 * @return {*} Returns converted value, or the same one if no conversion was necessary.
			 */
			coerceValueToModel: function (value, ModelConstructor, options) {

				if (!options) {
					options = {};
				}

				var self = this,
					methodName = 'coerceValueToModel',
					BackboneCollection = Backbone.Collection,
					isCollectionConstructor = (ModelConstructor === BackboneCollection) || (ModelConstructor.prototype instanceof BackboneCollection),
					baseObjectDefinition = options.baseObjectDefinition,
					allowEmptyModels = ('allowEmptyModels' in options) ? options.allowEmptyModels : true,
					baseObjectDefinitionIsEmpty,
					ret;

				// if value is undefined and we allow empty models
				if (allowEmptyModels && ((typeof value === 'undefined') || (value === null))) {
					value = isCollectionConstructor ? [] : {};
				}

				if (
					(typeof value !== 'undefined') && (value !== null) && // if the attribute is defined and non-null
						(!(value instanceof ModelConstructor))) { // or, if the callSettingsAndDiversionServiceModel is defined but not made of the right class, try to create it with the appropriate constructor

					//DEBUG
					//console.log(self.report(methodName, 'initial modelAttr='), modelAttr);

					if (isCollectionConstructor) { // if we're creating a collection
						var modelDefList = value;

						baseObjectDefinitionIsEmpty = $.isEmptyObject(baseObjectDefinition);

						if (modelDefList) { // if we have a list of objects
							if (!$.isArray(modelDefList)) {
								throw new Error(self.report(methodName, 'modelDefList is defined but it should have been an array, at least'));
							}

							if (!baseObjectDefinitionIsEmpty) {
								$.each(modelDefList, function (index, obj) {
									modelDefList[index] = $.extend(obj, baseObjectDefinition);
								});
							}

							ret = new ModelConstructor(modelDefList);
						} else {
							ret = new ModelConstructor(baseObjectDefinitionIsEmpty ? [] : baseObjectDefinition);
						}

					} else { // or a simple model

						//DEBUG
						//consolelog('new instance made with: %o', value);
						//consoledir(value);

						if (ModelConstructor === Date) {
							ret = new ModelConstructor(value);
						} else {
							ret = new ModelConstructor(
								$.extend(
									{},
									baseObjectDefinition || {},
									value || {}
								)
							);
						}
					}

					//DEBUG
					//consolelog(self.report(methodName, 'converted %o to %o'), value, ret);

				} else {
					ret = value;
				}

				return ret;
			}
		};
	}
);
